import React, { useEffect, useState } from 'react';
import DemographicsForm from './history/DemographicsForm';
import FamilyHistoryForm from './history/FamilyHistoryForm';
import PrenatalHistoryForm from './history/PrenatalHistoryForm';
import BirthHistoryForm from './history/BirthHistoryForm';
import DevelopmentalHistoryForm from './history/DevelopmentalHistoryForm';
import ImmunizationsForm from './history/ImmunizationsForm';
import PastMedicalHistoryForm from './history/PastMedicalHistoryForm';
import { getDoctorByEmail, getPatientsByDoctor, getPatientInfo } from '../api/database';
import { getPatientIntake, createPatientIntake, updatePatientIntake, type PatientIntake } from '../api/patientIntake';

type Page =
  | 'login'
  | 'register'
  | 'dashboard'
  | 'history'
  | 'timeline'
  | 'history-debug'
  | 'settings'
  | 'consent'
  | 'patients'
  | 'thresholds'
  | 'medication'
  | 'verify'
  | 'verify-patient'
  | 'reset-password-patient'
  | 'forgot-password'
  | 'reset-password';

type HistoryPageProps = {
  onLogout: () => void;
  onNavigate: (page: Page, options?: { keepPatient?: boolean }) => void;
  userRole?: string;
  isDemo?: boolean;
  /** When doctor: selected patient id; when set, show intake for that patient */
  patId?: string;
  /** When doctor: called when selecting a patient or clearing selection */
  onPatIdChange?: (patId: string) => void;
  /** Page to go back to when clicking back arrow (e.g. patients, dashboard) */
  previousPage?: Page;
};

const Section: React.FC<{
  title: string;
  children: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}> = ({ title, children, action, className }) => (
  <div className={`ph-section-card ${className || ''}`}>
    <div className="ph-section-header">
      <h3>{title}</h3>
      {action}
    </div>
    <div className="ph-top-grid ph-panel-body">
      {children}
    </div>
  </div>
);

const InfoItem: React.FC<{
  label: string;
  value?: string | number | boolean | null;
  fullWidth?: boolean;
}> = ({ label, value, fullWidth }) => {
  const display = value === true ? 'Yes' : value === false ? 'No' : value ?? '—';
  return (
    <div className={`ph-top-item${fullWidth ? ' ph-top-item-full' : ''}`}>
      <strong>{label}:</strong> {String(display)}
    </div>
  );
};

const formatWearableValue = (wearable?: boolean | null, brand?: string | null): string => {
  if (!wearable) return 'No';
  const b = brand?.trim();
  return b ? `Yes · ${b}` : 'Yes';
};

type IntakeFormData = Partial<Omit<PatientIntake, 'id' | 'pat_id' | 'created_at'>>;

const emptyIntakeForm = (): IntakeFormData => ({});

function intakeToFormData(i: PatientIntake): IntakeFormData {
  const { id, pat_id, created_at, ...rest } = i;
  const d: IntakeFormData = { ...rest };
  if (d.date_of_birth && typeof d.date_of_birth === 'string') {
    d.date_of_birth = d.date_of_birth.slice(0, 10);
  }
  return d;
}

const FormSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{
    backgroundColor: 'white',
    border: '1px solid #e9ecef',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '16px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
  }}>
    <h3 style={{ margin: '0 0 16px 0', fontSize: '1.05rem', fontWeight: '600', color: '#495057', borderBottom: '1px solid #eee', paddingBottom: '8px' }}>
      {title}
    </h3>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '12px 24px' }}>
      {children}
    </div>
  </div>
);

const FormField: React.FC<{
  label: string;
  value: string | undefined;
  onChange: (v: string) => void;
  type?: 'text' | 'email' | 'date';
  placeholder?: string;
  readOnly?: boolean;
}> = ({ label, value, onChange, type = 'text', placeholder, readOnly }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
    <label style={{ fontSize: '0.9rem', fontWeight: '500', color: '#495057' }}>{label}</label>
    <input
      type={type}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      readOnly={readOnly}
      style={{
        padding: '8px 12px',
        border: '1px solid #dee2e6',
        borderRadius: '8px',
        fontSize: '0.95rem',
        ...(readOnly ? { backgroundColor: '#e9ecef', cursor: 'not-allowed' } : {}),
      }}
    />
  </div>
);

const FormCheck: React.FC<{
  label: string;
  checked: boolean | null | undefined;
  onChange: (v: boolean) => void;
}> = ({ label, checked, onChange }) => (
  <div className="ph-form-check-wrap" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
    <input
      type="checkbox"
      checked={!!checked}
      onChange={(e) => onChange(e.target.checked)}
      id={label.replace(/\s/g, '_')}
    />
    <label htmlFor={label.replace(/\s/g, '_')} style={{ fontSize: '0.9rem', color: '#495057' }}>{label}</label>
  </div>
);

const CreateIntakeForm: React.FC<{
  patId: string;
  form: IntakeFormData;
  setForm: React.Dispatch<React.SetStateAction<IntakeFormData>>;
  onSubmit: () => void;
  onCancel: () => void;
  submitting: boolean;
  error: string | null;
  isEdit?: boolean;
  lockFullNameAndEmail?: boolean;
}> = ({ patId, form, setForm, onSubmit, onCancel, submitting, error, isEdit = false, lockFullNameAndEmail = false }) => {
  const update = <K extends keyof IntakeFormData>(k: K, v: IntakeFormData[K]) =>
    setForm((prev) => ({ ...prev, [k]: v }));

  return (
    <div style={{ maxWidth: '900px' }}>
      <div style={{ marginBottom: '20px' }}>
        <h2 style={{ margin: '0 0 8px 0', fontSize: '1.25rem', fontWeight: '600', color: '#2c3e50' }}>
          {isEdit ? 'Edit intake' : 'Add intake for patient'}
        </h2>
        <p style={{ margin: 0, color: '#6c757d', fontSize: '0.95rem' }}>
          Fill in the fields you need. All fields are optional.
        </p>
      </div>

      <FormSection title="Demographics">
        <FormField label="Full name" value={form.full_name ?? ''} onChange={(v) => update('full_name', v || undefined)} readOnly={lockFullNameAndEmail} />
        <FormField label="Date of birth" value={form.date_of_birth ?? ''} onChange={(v) => update('date_of_birth', v || undefined)} type="date" />
        <FormField label="Sex at birth" value={form.sex_at_birth ?? ''} onChange={(v) => update('sex_at_birth', v || undefined)} placeholder="e.g. Male, Female" />
        <FormField label="Phone" value={form.phone ?? ''} onChange={(v) => update('phone', v || undefined)} />
        <FormField label="Email" value={form.email ?? ''} onChange={(v) => update('email', v || undefined)} type="email" readOnly={lockFullNameAndEmail} />
        <FormField label="Preferred language" value={form.preferred_language ?? ''} onChange={(v) => update('preferred_language', v || undefined)} />
        <FormField label="Emergency contact name" value={form.emergency_contact_name ?? ''} onChange={(v) => update('emergency_contact_name', v || undefined)} />
        <FormField label="Emergency contact phone" value={form.emergency_contact_phone ?? ''} onChange={(v) => update('emergency_contact_phone', v || undefined)} />
      </FormSection>

      <FormSection title="Reason & symptoms">
        <FormField label="Reason" value={form.reason ?? ''} onChange={(v) => update('reason', v || undefined)} />
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={{ fontSize: '0.9rem', fontWeight: '500', color: '#495057' }}>Complaint</label>
          <textarea
            value={form.complaint ?? ''}
            onChange={(e) => update('complaint', e.target.value || undefined)}
            placeholder="Main complaint / description"
            rows={3}
            style={{ width: '100%', padding: '8px 12px', border: '1px solid #dee2e6', borderRadius: '8px', fontSize: '0.95rem', marginTop: '4px' }}
          />
        </div>
        <FormCheck label="Chest pain" checked={form.symptom_chest_pain} onChange={(v) => update('symptom_chest_pain', v)} />
        <FormCheck label="Shortness of breath" checked={form.symptom_shortness_breath} onChange={(v) => update('symptom_shortness_breath', v)} />
        <FormCheck label="Wheeze" checked={form.symptom_wheeze} onChange={(v) => update('symptom_wheeze', v)} />
        <FormCheck label="Cough" checked={form.symptom_cough} onChange={(v) => update('symptom_cough', v)} />
        <FormCheck label="Fever" checked={form.symptom_fever} onChange={(v) => update('symptom_fever', v)} />
        <FormCheck label="Palpitations" checked={form.symptom_palpitations} onChange={(v) => update('symptom_palpitations', v)} />
        <FormCheck label="Swelling legs" checked={form.symptom_swelling_legs} onChange={(v) => update('symptom_swelling_legs', v)} />
        <FormCheck label="Dizziness" checked={form.symptom_dizziness} onChange={(v) => update('symptom_dizziness', v)} />
        <FormCheck label="None" checked={form.symptom_none} onChange={(v) => update('symptom_none', v)} />
      </FormSection>

      <FormSection title="Conditions (cardiovascular & respiratory)">
        <FormCheck label="Hypertension" checked={form.hypertension} onChange={(v) => update('hypertension', v)} />
        <FormCheck label="High cholesterol" checked={form.high_cholesterol} onChange={(v) => update('high_cholesterol', v)} />
        <FormCheck label="CAD" checked={form.cad} onChange={(v) => update('cad', v)} />
        <FormCheck label="Heart failure" checked={form.heart_failure} onChange={(v) => update('heart_failure', v)} />
        <FormCheck label="Arrhythmia" checked={form.arrhythmia} onChange={(v) => update('arrhythmia', v)} />
        <FormCheck label="Stroke" checked={form.stroke} onChange={(v) => update('stroke', v)} />
        <FormCheck label="Asthma" checked={form.asthma} onChange={(v) => update('asthma', v)} />
        <FormCheck label="COPD" checked={form.copd} onChange={(v) => update('copd', v)} />
        <FormCheck label="Sleep apnea" checked={form.sleep_apnea} onChange={(v) => update('sleep_apnea', v)} />
        <FormCheck label="Chronic bronchitis" checked={form.chronic_bronchitis} onChange={(v) => update('chronic_bronchitis', v)} />
        <div style={{ gridColumn: '1 / -1' }}>
          <FormField label="Other lung disease" value={form.other_lung_disease ?? ''} onChange={(v) => update('other_lung_disease', v || undefined)} />
        </div>
      </FormSection>

      <FormSection title="Conditions (other)">
        <FormCheck label="Diabetes" checked={form.diabetes} onChange={(v) => update('diabetes', v)} />
        <FormCheck label="CKD" checked={form.ckd} onChange={(v) => update('ckd', v)} />
        <FormCheck label="Thyroid" checked={form.thyroid} onChange={(v) => update('thyroid', v)} />
        <FormCheck label="Obesity" checked={form.obesity} onChange={(v) => update('obesity', v)} />
        <FormCheck label="Depression/Anxiety" checked={form.depression_anxiety} onChange={(v) => update('depression_anxiety', v)} />
        <FormField label="Other condition" value={form.other_condition ?? ''} onChange={(v) => update('other_condition', v || undefined)} />
        <FormField label="Smoking status" value={form.smoking_status ?? ''} onChange={(v) => update('smoking_status', v || undefined)} placeholder="e.g. never, former, current" />
      </FormSection>

      <FormSection title="Medications & allergies">
        <FormCheck label="Takes medications" checked={form.takes_meds} onChange={(v) => update('takes_meds', v)} />
        <FormCheck label="Has allergies" checked={form.has_allergies} onChange={(v) => update('has_allergies', v)} />
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={{ fontSize: '0.9rem', fontWeight: '500', color: '#495057' }}>Allergies</label>
          <textarea
            value={form.allergies ?? ''}
            onChange={(e) => update('allergies', e.target.value || undefined)}
            placeholder="List allergies"
            rows={2}
            style={{ width: '100%', padding: '8px 12px', border: '1px solid #dee2e6', borderRadius: '8px', fontSize: '0.95rem', marginTop: '4px' }}
          />
        </div>
      </FormSection>

      <FormSection title="Devices">
        <FormCheck label="BP cuff" checked={form.bp_cuff} onChange={(v) => update('bp_cuff', v)} />
        <FormCheck label="Pulse oximeter" checked={form.pulse_ox} onChange={(v) => update('pulse_ox', v)} />
        <FormCheck label="Scale" checked={form.scale} onChange={(v) => update('scale', v)} />
        <FormCheck label="Inhaler" checked={form.inhaler} onChange={(v) => update('inhaler', v)} />
        <div className="ph-wearable-row" style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 10px', alignItems: 'end' }}>
          <div className="ph-form-check-wrap"><FormCheck label="Wearable" checked={form.wearable} onChange={(v) => update('wearable', v)} /></div>
          <FormField label="Brand" value={form.wearable_brand ?? ''} onChange={(v) => update('wearable_brand', v || undefined)} placeholder="Wearable brand" />
        </div>
      </FormSection>

      <FormSection title="Consent">
        <FormCheck label="Consent given" checked={form.consent} onChange={(v) => update('consent', v)} />
      </FormSection>

      {error && (
        <div style={{ color: '#dc3545', marginBottom: '16px', fontSize: '0.95rem' }}>{error}</div>
      )}
      <div style={{ display: 'flex', gap: '12px', marginTop: '24px', flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={onSubmit}
          disabled={submitting}
          style={{
            padding: '12px 24px',
            backgroundColor: '#1976d2',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontWeight: '600',
            cursor: submitting ? 'not-allowed' : 'pointer',
          }}
        >
          {submitting ? 'Saving...' : isEdit ? 'Save changes' : 'Save intake'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          style={{
            padding: '12px 24px',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: submitting ? 'not-allowed' : 'pointer',
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const EditablePanel: React.FC<{
  title: string;
  panelKey: string;
  editingPanel: string | null;
  onEdit: (panelKey: string) => void;
  onCancel: () => void;
  onSave: () => void;
  saving: boolean;
  viewContent: React.ReactNode;
  editContent: React.ReactNode;
}> = ({ title, panelKey, editingPanel, onEdit, onCancel, onSave, saving, viewContent, editContent }) => {
  const isEditing = editingPanel === panelKey;
  return (
    <div className={`ph-brick-panel${isEditing ? ' ph-brick-panel-active' : ''}`}>
      <Section
        title={title}
        action={
          !isEditing ? (
            <button type="button" className="ph-btn primary" onClick={() => onEdit(panelKey)}>Edit</button>
          ) : (
            <span className="ph-muted ph-small">Editing…</span>
          )
        }
      >
        {viewContent}
      </Section>
      {isEditing && (
        <div className="ph-edit-modal" role="dialog" aria-labelledby={`edit-${panelKey}`} onClick={(e) => e.stopPropagation()}>
            <Section
              title={`Edit — ${title}`}
              action={
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button type="button" className="ph-btn primary" onClick={onSave} disabled={saving}>
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                  <button type="button" className="ph-btn" onClick={onCancel} disabled={saving}>Cancel</button>
                </div>
              }
            >
              <div className="ph-edit-fields">{editContent}</div>
            </Section>
        </div>
      )}
    </div>
  );
};

const HistoryPage: React.FC<HistoryPageProps> = ({ onLogout, onNavigate, userRole, isDemo = false, patId, onPatIdChange }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const cards = [
    'Family History',
    'Prenatal History',
    'Birth History',
    'Developmental History',
    'Immunizations',
    'Past Medical History',
  ];
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const isDoctorHistory = Boolean(userRole === 'doctor' && !isDemo);
  const effectivePatId = patId || '';

  const [patients, setPatients] = useState<Array<{ pat_id: string; first_name: string; last_name: string; dev_id: string }>>([]);
  const [patientsLoading, setPatientsLoading] = useState(false);
  const [intake, setIntake] = useState<PatientIntake | null | undefined>(undefined);
  const [intakeLoading, setIntakeLoading] = useState(false);
  const [, setSelectedPatientName] = useState<string>('');
  const [showCreateIntakeForm, setShowCreateIntakeForm] = useState(false);
  const [intakeForm, setIntakeForm] = useState<IntakeFormData>(emptyIntakeForm);
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [isPatientModalOpen, setIsPatientModalOpen] = useState(() => !effectivePatId);
  const [showEditIntakeForm, setShowEditIntakeForm] = useState(false);
  const [editingPanel, setEditingPanel] = useState<string | null>(null);
  const [panelSaving, setPanelSaving] = useState(false);

  useEffect(() => {
    if (!isDoctorHistory) return;
    const loadPatients = async () => {
      setPatientsLoading(true);
      try {
        const email = localStorage.getItem('currentUserEmail') || '';
        if (!email) return;
        const doc = await getDoctorByEmail(email);
        if (!doc?.doc_id) return;
        const pats = await getPatientsByDoctor(doc.doc_id);
        setPatients(pats);
      } catch (e) {
        console.error('Error loading patients for history:', e);
      } finally {
        setPatientsLoading(false);
      }
    };
    loadPatients();
  }, [isDoctorHistory]);

  useEffect(() => {
    if (!isDoctorHistory) return;
    setIsPatientModalOpen(!effectivePatId);
  }, [isDoctorHistory, effectivePatId]);

  useEffect(() => {
    if (!isDoctorHistory || !effectivePatId) {
      setSelectedPatientName('');
      return;
    }
    const p = patients.find((x) => String(x.pat_id) === String(effectivePatId));
    setSelectedPatientName(p ? `${p.first_name} ${p.last_name}`.trim() || `Patient ${effectivePatId}` : `Patient ${effectivePatId}`);
  }, [patients, effectivePatId, isDoctorHistory]);

  useEffect(() => {
    if (!isDoctorHistory || !effectivePatId) {
      setIntake(undefined);
      return;
    }
    setIntakeLoading(true);
    setIntake(undefined);
    getPatientIntake(effectivePatId)
      .then((data) => {
        setIntake(data);
      })
      .catch(() => setIntake(null))
      .finally(() => setIntakeLoading(false));
  }, [isDoctorHistory, effectivePatId, patients]);

  // When Add intake form is shown, pre-fill name and email from patient info
  useEffect(() => {
    if (!showCreateIntakeForm || showEditIntakeForm || !effectivePatId) return;
    let cancelled = false;
    getPatientInfo(effectivePatId)
      .then((info) => {
        if (cancelled) return;
        const fullName = [info.first_name, info.last_name].filter(Boolean).join(' ').trim() || undefined;
        const email = info.email ?? undefined;
        setIntakeForm((prev) => ({
          ...prev,
          full_name: fullName ?? prev.full_name,
          email: email ?? prev.email,
        }));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [showCreateIntakeForm, showEditIntakeForm, effectivePatId]);

  const selectPatient = (id: string) => {
    setIsPatientModalOpen(false);
    onPatIdChange?.(id);
  };

  const startEditingHistory = async () => {
    if (!isDoctorHistory) return;
    setCreateError(null);
    if (intake) {
      setIntakeForm(intakeToFormData(intake));
      setShowCreateIntakeForm(false);
      setShowEditIntakeForm(true);
      return;
    }
    if (!effectivePatId) return;
    try {
      const info = await getPatientInfo(effectivePatId);
      const fullName = [info.first_name, info.last_name].filter(Boolean).join(' ').trim() || undefined;
      setIntakeForm((prev) => ({ ...prev, full_name: fullName, email: info.email ?? prev.email }));
    } catch (_) {
      setIntakeForm((prev) => prev);
    }
    setShowEditIntakeForm(false);
    setShowCreateIntakeForm(true);
  };

  const startPanelEditing = (panelKey: string) => {
    if (!intake) return;
    setEditingPanel(panelKey);
    setIntakeForm(intakeToFormData(intake));
    setCreateError(null);
  };

  const cancelPanelEditing = () => {
    setEditingPanel(null);
    setIntakeForm(intake ? intakeToFormData(intake) : emptyIntakeForm());
    setCreateError(null);
  };

  const savePanel = async () => {
    if (!intake) return;
    setPanelSaving(true);
    setCreateError(null);
    try {
      const { medications: _legacyMedicationField, ...intakePayload } = intakeForm;
      if (!effectivePatId) throw new Error('No patient selected');
      await updatePatientIntake(effectivePatId, intakePayload);
      const data = await getPatientIntake(effectivePatId);
      setIntake(data);
      setEditingPanel(null);
    } catch (e) {
      console.error('Error saving panel:', e);
      setCreateError(e instanceof Error ? e.message : 'Failed to save panel. Please try again.');
    } finally {
      setPanelSaving(false);
    }
  };

  const patientListModal = isDoctorHistory ? (
    <div
      className="modal-backdrop"
      onClick={() => setIsPatientModalOpen(false)}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        backdropFilter: 'blur(4px)',
      }}
    >
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          maxWidth: '500px',
          width: '90%',
          maxHeight: '80vh',
          overflow: 'hidden',
          animation: 'modalSlideIn 0.3s ease-out',
        }}
      >
        <div
          className="modal-header"
          style={{
            padding: '24px 24px 16px 24px',
            borderBottom: '1px solid #f0f0f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600', color: '#2c3e50', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '1.8rem' }}>🕒</span>
              Select Patient
            </h3>
            <p style={{ margin: '8px 0 0 0', color: '#7f8c8d', fontSize: '0.95rem', fontWeight: '400' }}>
              Choose which patient you want to view
            </p>
          </div>
          <button
            type="button"
            className="modal-close"
            onClick={() => setIsPatientModalOpen(false)}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.8rem',
              color: '#bdc3c7',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f8f9fa';
              e.currentTarget.style.color = '#e74c3c';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#bdc3c7';
            }}
          >
            ×
          </button>
        </div>
        <div className="modal-body" style={{ padding: '20px 24px', maxHeight: '400px', overflowY: 'auto' }}>
          {patientsLoading ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>Loading patients...</div>
          ) : patients.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>No patients found.</div>
          ) : (
            <div className="patient-list" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {patients.map((patient) => (
                <div
                  key={patient.pat_id}
                  className="patient-item"
                  onClick={() => selectPatient(patient.pat_id)}
                  style={{
                    padding: '20px',
                    border: '2px solid #ecf0f1',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    backgroundColor: '#fff',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#3498db';
                    e.currentTarget.style.backgroundColor = '#f8f9ff';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(52, 152, 219, 0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#ecf0f1';
                    e.currentTarget.style.backgroundColor = '#fff';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div
                      style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '1.2rem',
                        fontWeight: 'bold',
                        flexShrink: 0,
                      }}
                    >
                      {(patient.first_name?.charAt(0) || '?').toUpperCase()}
                      {(patient.last_name?.charAt(0) || '?').toUpperCase()}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '600', fontSize: '1.1rem', color: '#2c3e50', marginBottom: '4px' }}>
                        {[patient.first_name, patient.last_name].filter(Boolean).join(' ') || `Patient ${patient.pat_id}`}
                      </div>
                      <div style={{ fontSize: '0.9rem', color: '#7f8c8d', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ backgroundColor: '#ecf0f1', padding: '2px 8px', borderRadius: '12px', fontSize: '0.8rem', fontWeight: '500' }}>
                          ID: {patient.pat_id}
                        </span>
                      </div>
                    </div>
                    <div style={{ color: '#bdc3c7', fontSize: '1.2rem', transition: 'all 0.2s ease' }}>→</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  ) : null;

  const historyCss = `
    .ph-root * { box-sizing: border-box; }

    .ph-root {
      margin: 0;
      font-family: Arial, Helvetica, sans-serif;
      background: #f5f5f5;
      color: #1f2937;
      min-height: 100vh;
    }

    .ph-page {
      max-width: 1400px;
      margin: 0 auto;
      padding: 20px;
    }

    .ph-card {
      background: #ffffff;
      border: 1px solid #e5e7eb;
      border-radius: 18px;
      box-shadow: 0 8px 20px rgba(15, 23, 42, 0.06);
    }

    .ph-patient-header {
      padding: 12px 18px 16px;
      display: flex;
      flex-direction: column;
      gap: 8px;
      margin-bottom: 14px;
      min-height: 0;
      background: linear-gradient(145deg, #ffffff 0%, #f7fbff 100%);
    }

    .ph-patient-header .ph-section-header {
      margin-bottom: 0;
    }

    .ph-patient-main {
      display: flex;
      gap: 16px;
      align-items: center;
      min-width: 320px;
    }

    .ph-avatar {
      width: 58px;
      height: 58px;
      border-radius: 16px;
      background: #e8f7f6;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 28px;
      flex-shrink: 0;
    }

    .ph-root h1, .ph-root h2, .ph-root h3, .ph-root p { margin: 0; }

    .ph-muted { color: #64748b; }
    .ph-small { font-size: 13px; }

    .ph-badges {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      justify-content: flex-end;
    }

    .ph-badge {
      display: inline-flex;
      align-items: center;
      padding: 6px 10px;
      border-radius: 999px;
      font-size: 12px;
      font-weight: 700;
      background: #e8f7f6;
      color: #0f4f4b;
      white-space: nowrap;
    }

    .ph-badge.danger { background: #fee2e2; color: #991b1b; }
    .ph-badge.warning { background: #fef3c7; color: #92400e; }
    .ph-badge.success { background: #dcfce7; color: #166534; }

    .ph-status-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 16px;
      margin-bottom: 18px;
    }

    .ph-status-card {
      padding: 18px;
      display: flex;
      gap: 12px;
      align-items: center;
      min-height: 108px;
    }

    .ph-status-icon { font-size: 24px; }

    .ph-top-actions {
      display: flex;
      gap: 10px;
      flex-wrap: wrap;
      justify-content: flex-end;
      margin-left: auto;
    }

    .ph-btn {
      border: 1px solid #b7e3e0;
      border-radius: 12px;
      padding: 11px 12px;
      background: #ffffff;
      cursor: pointer;
      font-weight: 700;
      color: #0f4f4b;
      transition: all 0.2s ease;
    }

    .ph-btn.primary {
      background: #20B2AA;
      color: #ffffff;
      border-color: #20B2AA;
    }

    .ph-btn:hover {
      transform: translateY(-1px);
      box-shadow: 0 6px 14px rgba(15, 23, 42, 0.12);
    }

    .ph-tabs {
      display: grid;
      grid-template-columns: repeat(6, minmax(0, 1fr));
      gap: 6px;
      background: #d9f1ef;
      padding: 6px;
      border-radius: 16px;
      margin-bottom: 16px;
    }

    .ph-tab-button {
      border: none;
      border-radius: 12px;
      background: transparent;
      padding: 11px 8px;
      cursor: pointer;
      font-weight: 700;
      color: #0f4f4b;
    }

    .ph-tab-button.active {
      background: #ffffff;
      color: #0f4f4b;
      box-shadow: 0 4px 10px rgba(15, 23, 42, 0.08);
    }

    .ph-filters {
      padding: 16px;
      display: flex;
      align-items: center;
      flex-wrap: nowrap;
      gap: 10px;
      margin-bottom: 18px;
      overflow-x: auto;
    }

    .ph-input, .ph-select {
      width: 100%;
      border: 1px solid #b7e3e0;
      border-radius: 12px;
      padding: 11px 12px;
      background: #ffffff;
      font: inherit;
    }

    .ph-quick-ranges {
      display: flex;
      gap: 8px;
      flex-wrap: nowrap;
      align-items: center;
    }

    .ph-search-input {
      flex: 0 0 33%;
      min-width: 220px;
    }

    .ph-date-input {
      flex: 0 0 150px;
      min-width: 140px;
    }

    .ph-timeline-row {
      display: grid;
      grid-template-columns: 2.2fr 1.5fr 1fr;
      gap: 10px;
      align-items: center;
      width: 100%;
    }

    .ph-center-actions {
      display: flex;
      justify-content: center;
      gap: 10px;
    }

    .ph-btn.quick-active {
      background: #111111;
      color: #ffffff;
      border-color: #111111;
    }

    .ph-workspace {
      display: grid;
      grid-template-columns: 5fr 7fr;
      gap: 18px;
      height: calc(100vh - 300px);
      min-height: 520px;
      overflow: hidden;
    }

    .ph-workspace > aside {
      display: flex;
      flex-direction: column;
      min-height: 0;
      overflow: hidden;
    }

    .ph-workspace > article {
      position: sticky;
      top: 0;
      align-self: start;
      height: 100%;
      min-height: 0;
      overflow-y: auto;
    }

    .ph-panel-title { padding: 20px 20px 0 20px; }

    .ph-timeline-list {
      padding: 20px;
      display: flex;
      flex-direction: column;
      gap: 14px;
      overflow-y: auto;
      min-height: 0;
      flex: 1 1 auto;
    }

    .ph-timeline-card {
      width: 100%;
      text-align: left;
      border: 1px solid #e5e7eb;
      background: #f7fdfc;
      border-radius: 16px;
      padding: 16px;
      cursor: pointer;
      transition: 0.15s ease;
    }

    .ph-timeline-card:hover,
    .ph-timeline-card.active {
      background: #ffffff;
      border-color: #20B2AA;
      box-shadow: 0 8px 16px rgba(32, 178, 170, 0.15);
    }

    .ph-timeline-head {
      display: flex;
      justify-content: space-between;
      gap: 8px;
      margin-bottom: 8px;
      align-items: center;
    }

    .ph-record-detail { padding: 22px; }

    .ph-detail-header {
      display: flex;
      justify-content: space-between;
      gap: 16px;
      margin-bottom: 20px;
      align-items: flex-start;
    }

    .ph-info-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 12px;
      margin-bottom: 16px;
    }

    .ph-info-box {
      background: #f0faf9;
      border-radius: 14px;
      padding: 14px;
    }

    .ph-section-box {
      border: 1px solid #e5e7eb;
      border-radius: 16px;
      padding: 16px;
      margin-top: 16px;
    }

    .ph-table {
      width: 100%;
      border-collapse: collapse;
      overflow: hidden;
      border-radius: 14px;
      font-size: 14px;
    }

    .ph-table th, .ph-table td {
      padding: 12px;
      border-bottom: 1px solid #e5e7eb;
      text-align: left;
    }

    .ph-table th { background: #eaf7f5; }

    .ph-two-col {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 16px;
      margin-top: 16px;
    }

    .ph-trend {
      height: 110px;
      background: #f0faf9;
      border-radius: 14px;
      padding: 14px;
      display: flex;
      align-items: end;
      gap: 9px;
    }

    .ph-bar {
      flex: 1;
      background: #87CEEB;
      border-radius: 8px 8px 0 0;
    }

    .ph-grid-cards {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 16px;
    }

    .ph-bricks {
      display: flex;
      flex-direction: column;
      gap: 14px;
    }

    .ph-bricks-columns {
      display: flex;
      gap: 14px;
      align-items: flex-start;
    }

    .ph-bricks-col {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 14px;
    }

    .ph-bricks-col .ph-section-card {
      margin-bottom: 0;
    }

    .ph-bricks-error {
      width: 100%;
      margin-bottom: 0;
    }

    .ph-edit-backdrop {
      position: fixed;
      inset: 0;
      background: rgba(15, 23, 42, 0.45);
      z-index: 1200;
    }

    .ph-edit-modal {
      position: fixed;
      z-index: 1201;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: min(1050px, 94vw);
      max-height: 86vh;
      overflow-y: auto;
      padding: 8px;
    }

    .ph-edit-modal .ph-section-card {
      border-radius: 18px;
      box-shadow: 0 20px 50px rgba(15, 23, 42, 0.25);
      border: 1px solid #dbeafe;
    }

    .ph-section-card {
      background: #ffffff;
      border: 1px solid #e9ecef;
      border-radius: 12px;
      padding: 12px 18px 16px;
      margin-bottom: 16px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
      transition: box-shadow 0.2s ease, transform 0.2s ease;
    }

    .ph-section-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 12px;
      margin-bottom: 6px;
      padding-bottom: 5px;
      border-bottom: 1px solid #eee;
    }

    .ph-section-header h3 {
      margin: 0;
      font-size: 1.05rem;
      font-weight: 600;
      color: #495057;
    }

    .ph-section-card:hover {
      box-shadow: 0 12px 28px rgba(15, 23, 42, 0.11);
      transform: translateY(-2px);
    }

    .ph-top-grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 8px 20px;
      margin-top: 0;
      font-size: 0.92rem;
    }

    .ph-top-item {
      background: #f8fbff;
      border: 1px solid #e5edf8;
      border-radius: 10px;
      padding: 8px 10px;
      color: #334155;
      line-height: 1.4;
    }

    .ph-top-item-full {
      grid-column: 1 / -1;
    }

    .ph-panel-body {
      margin-top: 0;
    }

    .ph-brick-panel {
      width: 100%;
      min-width: 0;
    }

    .ph-brick-panel-active .ph-section-card,
    .ph-patient-header.ph-brick-panel-active {
      outline: 2px solid #20B2AA;
      outline-offset: 2px;
    }

    .ph-edit-fields {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 12px 16px;
    }

    .ph-edit-fields .ph-form-check-wrap {
      margin-top: 0;
    }

    .ph-wearable-row {
      grid-column: 1 / -1;
      display: grid;
      grid-template-columns: auto 1fr;
      gap: 8px 10px;
      align-items: end;
    }

    .ph-form-check-wrap {
      margin-top: 0;
    }

    .ph-header-edit-wrap {
      display: flex;
      justify-content: flex-end;
      align-items: flex-start;
    }

    .ph-content-card { padding: 18px; }
    .ph-list { display: grid; gap: 12px; }
    .ph-list.two-col { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .ph-list.two-col > h2,
    .ph-list.two-col > p {
      grid-column: 1 / -1;
    }

    .ph-list-item {
      border: 1px solid #e5e7eb;
      border-radius: 16px;
      padding: 16px;
      background: #ffffff;
    }

    .ph-list-item.full-width {
      grid-column: 1 / -1;
    }

    .ph-mark {
      background: #fff59d;
      color: inherit;
      padding: 0;
    }

    .ph-tab-panel {
      min-height: 52vh;
    }

    .ph-tab-panel .ph-content-card {
      min-height: 52vh;
    }

    .ph-actions {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
      margin-top: 16px;
    }

    .ph-timeline-toolbar {
      display: grid;
      grid-template-columns: minmax(180px, 2fr) auto auto auto auto auto auto auto;
      gap: 8px;
      align-items: center;
    }

    @media (max-width: 1000px) {
      .ph-patient-header,
      .ph-detail-header {
        flex-direction: column;
        align-items: flex-start;
      }

      .ph-status-grid,
      .ph-workspace,
      .ph-two-col,
      .ph-info-grid,
      .ph-grid-cards,
      .ph-bricks-columns {
        flex-direction: column;
      }

      .ph-edit-fields {
        grid-template-columns: 1fr;
      }

      .ph-top-grid {
        grid-template-columns: 1fr;
      }

      .ph-workspace {
        height: auto;
        min-height: 0;
        overflow: visible;
      }

      .ph-workspace > aside,
      .ph-workspace > article {
        position: static;
        height: auto;
        overflow: visible;
      }

      .ph-filters {
        flex-wrap: wrap;
      }

      .ph-search-input,
      .ph-date-input {
        flex: 1 1 100%;
      }

      .ph-timeline-row {
        grid-template-columns: 1fr;
      }

      .ph-list.two-col {
        grid-template-columns: 1fr;
      }

      .ph-tabs {
        grid-template-columns: repeat(2, 1fr);
      }

      .ph-badges {
        justify-content: flex-start;
      }

      .ph-timeline-toolbar {
        grid-template-columns: 1fr;
      }
    }
  `;

  return (
    <div className="dashboard">
      <header className="header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="hamburger-btn" onClick={() => setIsSidebarOpen(true)} aria-label="Open menu">☰</button>
          <h1>History</h1>
        </div>
        <div />
      </header>
      <div className={`sidebar ${isSidebarOpen ? 'open' : ''}`} role="navigation" aria-label="Main menu">
        <ul className="sidebar-menu">
          <li onClick={() => { setIsSidebarOpen(false); if (userRole === 'doctor' && !isDemo) onNavigate('patients'); else onNavigate('dashboard'); }}>
            <span className="mi">🏠</span><span>Overview</span>
          </li>
          <li onClick={() => setIsSidebarOpen(false)}><span className="mi">🕒</span><span>History</span></li>
          {isDoctorHistory && (
            <li onClick={() => { setIsSidebarOpen(false); onNavigate('timeline'); }}><span className="mi">📈</span><span>Timeline</span></li>
          )}
          <li onClick={() => { setIsSidebarOpen(false); onNavigate('settings'); }}><span className="mi">⚙️</span><span>Settings</span></li>
          <li onClick={() => { setIsSidebarOpen(false); onNavigate('consent'); }}><span className="mi">📝</span><span>Consent</span></li>
          <li onClick={() => { setIsSidebarOpen(false); onLogout(); }}><span className="mi">↪</span><span>Logout</span></li>
        </ul>
        <div className="sidebar-footer"><div className="sidebar-brand"></div></div>
      </div>
      <div className={`sidebar-backdrop ${isSidebarOpen ? 'show' : ''}`} onClick={() => setIsSidebarOpen(false)} />

      <div className="ph-root">
      <style>{historyCss}</style>
      <main className="ph-page">
        {isDoctorHistory && (
          <section className={`ph-card ph-patient-header${editingPanel === 'demographics' ? ' ph-brick-panel-active' : ''}`}>
            {intake && (
              <div className="ph-section-header">
                <h3>Demographics</h3>
                <div className="ph-header-edit-wrap">
                  {editingPanel !== 'demographics' ? (
                    <button type="button" className="ph-btn primary" onClick={() => startPanelEditing('demographics')}>Edit</button>
                  ) : (
                    <span className="ph-muted ph-small">Editing…</span>
                  )}
                </div>
              </div>
            )}
            <div className="ph-patient-main">
              <div className="ph-avatar">👤</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                {intake && (
                  <div className="ph-top-grid ph-panel-body">
                    <InfoItem label="Name" value={intake.full_name} />
                    <InfoItem label="DOB" value={intake.date_of_birth ? new Date(intake.date_of_birth).toLocaleDateString() : null} />
                    <InfoItem label="Sex" value={intake.sex_at_birth} />
                    <InfoItem label="Phone" value={intake.phone} />
                    <InfoItem label="Email" value={intake.email} />
                    <InfoItem label="Language" value={intake.preferred_language} />
                    <InfoItem
                      label="Emergency"
                      value={`${intake.emergency_contact_name || '—'} (${intake.emergency_contact_phone || '—'})`}
                      fullWidth
                    />
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {editingPanel && (
          <div className="ph-edit-backdrop" onClick={cancelPanelEditing} aria-hidden="true" />
        )}
        {editingPanel === 'demographics' && intake && (
          <div className="ph-edit-modal" role="dialog" aria-label="Edit demographics" onClick={(e) => e.stopPropagation()}>
            <Section
              title="Edit — Patient demographics"
              action={
                <div style={{ display: 'flex', gap: 10 }}>
                  <button type="button" className="ph-btn primary" onClick={savePanel} disabled={panelSaving}>
                    {panelSaving ? 'Saving...' : 'Save'}
                  </button>
                  <button type="button" className="ph-btn" onClick={cancelPanelEditing} disabled={panelSaving}>Cancel</button>
                </div>
              }
            >
              <div className="ph-edit-fields">
                <FormField label="Full name" value={intakeForm.full_name ?? ''} onChange={(v) => setIntakeForm((prev) => ({ ...prev, full_name: v || undefined }))} />
                <FormField label="Date of birth" value={intakeForm.date_of_birth ?? ''} onChange={(v) => setIntakeForm((prev) => ({ ...prev, date_of_birth: v || undefined }))} type="date" />
                <FormField label="Sex at birth" value={intakeForm.sex_at_birth ?? ''} onChange={(v) => setIntakeForm((prev) => ({ ...prev, sex_at_birth: v || undefined }))} />
                <FormField label="Phone" value={intakeForm.phone ?? ''} onChange={(v) => setIntakeForm((prev) => ({ ...prev, phone: v || undefined }))} />
                <FormField label="Email" value={intakeForm.email ?? ''} onChange={(v) => setIntakeForm((prev) => ({ ...prev, email: v || undefined }))} type="email" />
                <FormField label="Preferred language" value={intakeForm.preferred_language ?? ''} onChange={(v) => setIntakeForm((prev) => ({ ...prev, preferred_language: v || undefined }))} />
                <FormField label="Emergency contact name" value={intakeForm.emergency_contact_name ?? ''} onChange={(v) => setIntakeForm((prev) => ({ ...prev, emergency_contact_name: v || undefined }))} />
                <FormField label="Emergency contact phone" value={intakeForm.emergency_contact_phone ?? ''} onChange={(v) => setIntakeForm((prev) => ({ ...prev, emergency_contact_phone: v || undefined }))} />
              </div>
            </Section>
          </div>
        )}

        {/* Hidden by request: old tabs/timeline/search UI */}
        {/* <nav className="ph-tabs" aria-label="Patient history tabs" /> */}
        {/* <section className="ph-card ph-filters" aria-label="Filters" /> */}

        <section aria-label="Panels">
          {isDoctorHistory && (
            <>
              {intakeLoading ? (
                <p className="ph-muted">Loading...</p>
              ) : showCreateIntakeForm || showEditIntakeForm ? (
                <CreateIntakeForm
                  patId={effectivePatId}
                  form={intakeForm}
                  setForm={setIntakeForm}
                  submitting={createSubmitting}
                  error={createError}
                  lockFullNameAndEmail={true}
                  isEdit={showEditIntakeForm}
                  onCancel={() => {
                    setShowCreateIntakeForm(false);
                    setShowEditIntakeForm(false);
                    setCreateError(null);
                    setIntakeForm(intake ? intakeToFormData(intake) : emptyIntakeForm());
                  }}
                  onSubmit={async () => {
                    setCreateSubmitting(true);
                    setCreateError(null);
                    try {
                      const { medications: _legacyMedicationField, ...intakePayload } = intakeForm;
                      if (showEditIntakeForm && intake) {
                        await updatePatientIntake(effectivePatId, intakePayload);
                      } else {
                        await createPatientIntake(effectivePatId, intakePayload);
                      }
                      const data = await getPatientIntake(effectivePatId);
                      setIntake(data);
                      setShowCreateIntakeForm(false);
                      setShowEditIntakeForm(false);
                    } catch (e) {
                      console.error('Error saving intake:', e);
                      setCreateError('Failed to save intake. Please try again.');
                    } finally {
                      setCreateSubmitting(false);
                    }
                  }}
                />
              ) : intake === null ? (
                <div className="ph-card" style={{ padding: 18 }}>
                  <p className="ph-muted" style={{ marginBottom: 12 }}>No intake recorded for this patient yet. You can add one now.</p>
                  <button type="button" className="ph-btn primary" onClick={startEditingHistory}>Edit</button>
                </div>
              ) : intake ? (
                <div className="ph-bricks">
                  {createError && (
                    <p className="ph-muted ph-bricks-error" style={{ color: '#dc3545' }}>{createError}</p>
                  )}
                  {/* Left column: symptoms, conditions-other, consent | Right: cardiorespiratory, medications, devices */}
                  <div className="ph-bricks-columns">
                  <div className="ph-bricks-col">
                  <EditablePanel
                    title="Reason & symptoms"
                    panelKey="symptoms"
                    editingPanel={editingPanel}
                    onEdit={startPanelEditing}
                    onCancel={cancelPanelEditing}
                    onSave={savePanel}
                    saving={panelSaving}
                    viewContent={
                      <>
                        <InfoItem label="Reason" value={intake.reason} />
                        <InfoItem label="Complaint" value={intake.complaint} fullWidth />
                        <InfoItem label="Chest pain" value={intake.symptom_chest_pain} />
                        <InfoItem label="Shortness of breath" value={intake.symptom_shortness_breath} />
                        <InfoItem label="Wheeze" value={intake.symptom_wheeze} />
                        <InfoItem label="Cough" value={intake.symptom_cough} />
                        <InfoItem label="Fever" value={intake.symptom_fever} />
                        <InfoItem label="Palpitations" value={intake.symptom_palpitations} />
                        <InfoItem label="Swelling legs" value={intake.symptom_swelling_legs} />
                        <InfoItem label="Dizziness" value={intake.symptom_dizziness} />
                      </>
                    }
                    editContent={
                      <>
                        <FormField label="Reason" value={intakeForm.reason ?? ''} onChange={(v) => setIntakeForm((prev) => ({ ...prev, reason: v || undefined }))} />
                        <FormField label="Complaint" value={intakeForm.complaint ?? ''} onChange={(v) => setIntakeForm((prev) => ({ ...prev, complaint: v || undefined }))} />
                        <div className="ph-form-check-wrap"><FormCheck label="Chest pain" checked={intakeForm.symptom_chest_pain} onChange={(v) => setIntakeForm((prev) => ({ ...prev, symptom_chest_pain: v }))} /></div>
                        <div className="ph-form-check-wrap"><FormCheck label="Shortness of breath" checked={intakeForm.symptom_shortness_breath} onChange={(v) => setIntakeForm((prev) => ({ ...prev, symptom_shortness_breath: v }))} /></div>
                        <div className="ph-form-check-wrap"><FormCheck label="Wheeze" checked={intakeForm.symptom_wheeze} onChange={(v) => setIntakeForm((prev) => ({ ...prev, symptom_wheeze: v }))} /></div>
                        <div className="ph-form-check-wrap"><FormCheck label="Cough" checked={intakeForm.symptom_cough} onChange={(v) => setIntakeForm((prev) => ({ ...prev, symptom_cough: v }))} /></div>
                        <div className="ph-form-check-wrap"><FormCheck label="Fever" checked={intakeForm.symptom_fever} onChange={(v) => setIntakeForm((prev) => ({ ...prev, symptom_fever: v }))} /></div>
                        <div className="ph-form-check-wrap"><FormCheck label="Palpitations" checked={intakeForm.symptom_palpitations} onChange={(v) => setIntakeForm((prev) => ({ ...prev, symptom_palpitations: v }))} /></div>
                        <div className="ph-form-check-wrap"><FormCheck label="Swelling legs" checked={intakeForm.symptom_swelling_legs} onChange={(v) => setIntakeForm((prev) => ({ ...prev, symptom_swelling_legs: v }))} /></div>
                        <div className="ph-form-check-wrap"><FormCheck label="Dizziness" checked={intakeForm.symptom_dizziness} onChange={(v) => setIntakeForm((prev) => ({ ...prev, symptom_dizziness: v }))} /></div>
                      </>
                    }
                  />

                  <EditablePanel
                    title="Conditions (other)"
                    panelKey="conditions-other"
                    editingPanel={editingPanel}
                    onEdit={startPanelEditing}
                    onCancel={cancelPanelEditing}
                    onSave={savePanel}
                    saving={panelSaving}
                    viewContent={
                      <>
                        <InfoItem label="Diabetes" value={intake.diabetes} />
                        <InfoItem label="CKD" value={intake.ckd} />
                        <InfoItem label="Thyroid" value={intake.thyroid} />
                        <InfoItem label="Obesity" value={intake.obesity} />
                        <InfoItem label="Depression/Anxiety" value={intake.depression_anxiety} />
                        <InfoItem label="Smoking status" value={intake.smoking_status} />
                        <InfoItem label="Other condition" value={intake.other_condition} fullWidth />
                      </>
                    }
                    editContent={
                      <>
                        <div className="ph-form-check-wrap"><FormCheck label="Diabetes" checked={intakeForm.diabetes} onChange={(v) => setIntakeForm((prev) => ({ ...prev, diabetes: v }))} /></div>
                        <div className="ph-form-check-wrap"><FormCheck label="CKD" checked={intakeForm.ckd} onChange={(v) => setIntakeForm((prev) => ({ ...prev, ckd: v }))} /></div>
                        <div className="ph-form-check-wrap"><FormCheck label="Thyroid" checked={intakeForm.thyroid} onChange={(v) => setIntakeForm((prev) => ({ ...prev, thyroid: v }))} /></div>
                        <div className="ph-form-check-wrap"><FormCheck label="Obesity" checked={intakeForm.obesity} onChange={(v) => setIntakeForm((prev) => ({ ...prev, obesity: v }))} /></div>
                        <div className="ph-form-check-wrap"><FormCheck label="Depression/Anxiety" checked={intakeForm.depression_anxiety} onChange={(v) => setIntakeForm((prev) => ({ ...prev, depression_anxiety: v }))} /></div>
                        <FormField label="Smoking status" value={intakeForm.smoking_status ?? ''} onChange={(v) => setIntakeForm((prev) => ({ ...prev, smoking_status: v || undefined }))} />
                        <FormField label="Other condition" value={intakeForm.other_condition ?? ''} onChange={(v) => setIntakeForm((prev) => ({ ...prev, other_condition: v || undefined }))} />
                      </>
                    }
                  />

                  <EditablePanel
                    title="Consent"
                    panelKey="consent"
                    editingPanel={editingPanel}
                    onEdit={startPanelEditing}
                    onCancel={cancelPanelEditing}
                    onSave={savePanel}
                    saving={panelSaving}
                    viewContent={<InfoItem label="Consent given" value={intake.consent} />}
                    editContent={
                      <div className="ph-form-check-wrap">
                        <FormCheck label="Consent given" checked={intakeForm.consent} onChange={(v) => setIntakeForm((prev) => ({ ...prev, consent: v }))} />
                      </div>
                    }
                  />
                  </div>

                  <div className="ph-bricks-col">
                  <EditablePanel
                    title="Conditions (cardiovascular & respiratory)"
                    panelKey="cardiorespiratory"
                    editingPanel={editingPanel}
                    onEdit={startPanelEditing}
                    onCancel={cancelPanelEditing}
                    onSave={savePanel}
                    saving={panelSaving}
                    viewContent={
                      <>
                        <InfoItem label="Hypertension" value={intake.hypertension} />
                        <InfoItem label="High cholesterol" value={intake.high_cholesterol} />
                        <InfoItem label="CAD" value={intake.cad} />
                        <InfoItem label="Heart failure" value={intake.heart_failure} />
                        <InfoItem label="Arrhythmia" value={intake.arrhythmia} />
                        <InfoItem label="Stroke" value={intake.stroke} />
                        <InfoItem label="Asthma" value={intake.asthma} />
                        <InfoItem label="COPD" value={intake.copd} />
                        <InfoItem label="Sleep apnea" value={intake.sleep_apnea} />
                        <InfoItem label="Chronic bronchitis" value={intake.chronic_bronchitis} />
                        <InfoItem label="Other lung disease" value={intake.other_lung_disease} fullWidth />
                      </>
                    }
                    editContent={
                      <>
                        <div className="ph-form-check-wrap"><FormCheck label="Hypertension" checked={intakeForm.hypertension} onChange={(v) => setIntakeForm((prev) => ({ ...prev, hypertension: v }))} /></div>
                        <div className="ph-form-check-wrap"><FormCheck label="High cholesterol" checked={intakeForm.high_cholesterol} onChange={(v) => setIntakeForm((prev) => ({ ...prev, high_cholesterol: v }))} /></div>
                        <div className="ph-form-check-wrap"><FormCheck label="CAD" checked={intakeForm.cad} onChange={(v) => setIntakeForm((prev) => ({ ...prev, cad: v }))} /></div>
                        <div className="ph-form-check-wrap"><FormCheck label="Heart failure" checked={intakeForm.heart_failure} onChange={(v) => setIntakeForm((prev) => ({ ...prev, heart_failure: v }))} /></div>
                        <div className="ph-form-check-wrap"><FormCheck label="Arrhythmia" checked={intakeForm.arrhythmia} onChange={(v) => setIntakeForm((prev) => ({ ...prev, arrhythmia: v }))} /></div>
                        <div className="ph-form-check-wrap"><FormCheck label="Stroke" checked={intakeForm.stroke} onChange={(v) => setIntakeForm((prev) => ({ ...prev, stroke: v }))} /></div>
                        <div className="ph-form-check-wrap"><FormCheck label="Asthma" checked={intakeForm.asthma} onChange={(v) => setIntakeForm((prev) => ({ ...prev, asthma: v }))} /></div>
                        <div className="ph-form-check-wrap"><FormCheck label="COPD" checked={intakeForm.copd} onChange={(v) => setIntakeForm((prev) => ({ ...prev, copd: v }))} /></div>
                        <div className="ph-form-check-wrap"><FormCheck label="Sleep apnea" checked={intakeForm.sleep_apnea} onChange={(v) => setIntakeForm((prev) => ({ ...prev, sleep_apnea: v }))} /></div>
                        <div className="ph-form-check-wrap"><FormCheck label="Chronic bronchitis" checked={intakeForm.chronic_bronchitis} onChange={(v) => setIntakeForm((prev) => ({ ...prev, chronic_bronchitis: v }))} /></div>
                        <FormField label="Other lung disease" value={intakeForm.other_lung_disease ?? ''} onChange={(v) => setIntakeForm((prev) => ({ ...prev, other_lung_disease: v || undefined }))} />
                      </>
                    }
                  />

                  <EditablePanel
                    title="Medications & allergies"
                    panelKey="medications-allergies"
                    editingPanel={editingPanel}
                    onEdit={startPanelEditing}
                    onCancel={cancelPanelEditing}
                    onSave={savePanel}
                    saving={panelSaving}
                    viewContent={
                      <>
                        <InfoItem label="Takes medications" value={intake.takes_meds} />
                        {intake.has_allergies ? (
                          <InfoItem label="Allergies" value={intake.allergies || 'Reported'} fullWidth />
                        ) : (
                          <InfoItem label="Has allergies" value={false} />
                        )}
                      </>
                    }
                    editContent={
                      <>
                        <div className="ph-form-check-wrap"><FormCheck label="Takes medications" checked={intakeForm.takes_meds} onChange={(v) => setIntakeForm((prev) => ({ ...prev, takes_meds: v }))} /></div>
                        <div className="ph-form-check-wrap"><FormCheck label="Has allergies" checked={intakeForm.has_allergies} onChange={(v) => setIntakeForm((prev) => ({ ...prev, has_allergies: v }))} /></div>
                        <FormField label="Allergies" value={intakeForm.allergies ?? ''} onChange={(v) => setIntakeForm((prev) => ({ ...prev, allergies: v || undefined }))} />
                      </>
                    }
                  />

                  <EditablePanel
                    title="Devices"
                    panelKey="devices"
                    editingPanel={editingPanel}
                    onEdit={startPanelEditing}
                    onCancel={cancelPanelEditing}
                    onSave={savePanel}
                    saving={panelSaving}
                    viewContent={
                      <>
                        <InfoItem label="BP cuff" value={intake.bp_cuff} />
                        <InfoItem label="Pulse oximeter" value={intake.pulse_ox} />
                        <InfoItem label="Scale" value={intake.scale} />
                        <InfoItem label="Inhaler" value={intake.inhaler} />
                        <InfoItem label="Wearable" value={formatWearableValue(intake.wearable, intake.wearable_brand)} />
                      </>
                    }
                    editContent={
                      <>
                        <div className="ph-form-check-wrap"><FormCheck label="BP cuff" checked={intakeForm.bp_cuff} onChange={(v) => setIntakeForm((prev) => ({ ...prev, bp_cuff: v }))} /></div>
                        <div className="ph-form-check-wrap"><FormCheck label="Pulse oximeter" checked={intakeForm.pulse_ox} onChange={(v) => setIntakeForm((prev) => ({ ...prev, pulse_ox: v }))} /></div>
                        <div className="ph-form-check-wrap"><FormCheck label="Scale" checked={intakeForm.scale} onChange={(v) => setIntakeForm((prev) => ({ ...prev, scale: v }))} /></div>
                        <div className="ph-form-check-wrap"><FormCheck label="Inhaler" checked={intakeForm.inhaler} onChange={(v) => setIntakeForm((prev) => ({ ...prev, inhaler: v }))} /></div>
                        <div className="ph-wearable-row">
                          <div className="ph-form-check-wrap"><FormCheck label="Wearable" checked={intakeForm.wearable} onChange={(v) => setIntakeForm((prev) => ({ ...prev, wearable: v }))} /></div>
                          <FormField label="Brand" value={intakeForm.wearable_brand ?? ''} onChange={(v) => setIntakeForm((prev) => ({ ...prev, wearable_brand: v || undefined }))} placeholder="Wearable brand" />
                        </div>
                      </>
                    }
                  />
                  </div>
                  </div>
                </div>
              ) : (
                <p className="ph-muted">Select a patient to view intake.</p>
              )}
            </>
          )}

          {!isDoctorHistory && (
            <div className="ph-card" style={{ padding: 18 }}>
              <h2 style={{ marginBottom: 10 }}>Questionnaire</h2>
              <p className="ph-muted ph-small" style={{ marginBottom: 14 }}>
                Please complete this form and bring it with you at the time of your appointment.
              </p>
              <div className="accordion">
                {cards.map((title, idx) => (
                  <div className="accordion-item" key={title}>
                    <button className="accordion-header" onClick={() => setOpenIndex(openIndex === idx ? null : idx)}>
                      <span>{title}</span>
                      <span>{openIndex === idx ? '▴' : '▾'}</span>
                    </button>
                    {openIndex === idx && (
                      <div className="accordion-body">
                        {idx === 0 ? (
                          <DemographicsForm />
                        ) : idx === 1 ? (
                          <FamilyHistoryForm />
                        ) : idx === 2 ? (
                          <PrenatalHistoryForm />
                        ) : idx === 3 ? (
                          <BirthHistoryForm />
                        ) : idx === 4 ? (
                          <DevelopmentalHistoryForm />
                        ) : idx === 5 ? (
                          <ImmunizationsForm />
                        ) : idx === 6 ? (
                          <PastMedicalHistoryForm />
                        ) : (
                          <div style={{ minHeight: 80 }} />
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      </main>
      </div>
      {isDoctorHistory && isPatientModalOpen && patientListModal}
    </div>
  );
};

export default HistoryPage;
